import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Stack, useTheme } from "@mui/material"

import usePowerline from "helpers/hooks/powerlines/usePowerline"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"
import LinkTabsSection from "widgets/common/LinkTabsSection"
import { getPowerlineTabsConfig } from "helpers/config/tabs/powerlineTabsConfig"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import { entityHasTriggeredAlerts } from "helpers/utils/alerts"
import Spinner from "widgets/common/Spinner"
import PreviousPage from "widgets/common/PreviousPage"
import useRoutes from "helpers/hooks/useRoutes"
import DashboardDatePicker from "widgets/common/dashboard/DashboardDatePicker"
import useDateRange from "helpers/hooks/useDateRange"
import ScrollToTopButton from "widgets/common/ScrollToTopButton"
import { DEFAULT_TARGET } from "helpers/utils/constants"
import PowerlineSummary from "widgets/powerlines/PowerlineSummary"
import ExitZoomModeButton from "widgets/common/ExitZoomModeButton"

const Powerline = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { id } = useParams()
  const {
    powerline,
    isFetching: powerlineFetching,
    isError,
  } = usePowerline({ id: Number(id) })

  useDocumentTitle(powerline?.name || t("pageTitles.POWERLINE"))

  const { baseOrgURL } = useRoutes()
  const { getDateRangeTools, arePlotsLoading } = useDateRange()
  const {
    dateRange: powerlineDateRange,
    dispatchDateRange: dispatchPowerlineDateRange,
    searchParams: powerlineSearchParams,
  } = getDateRangeTools("powerline")
  const navigate = useNavigate()

  const { getPowerlineAlertSettingsWStatesByEntityId } = useAlertSettingsWStates()
  const { orgActivePowerlinesAlertSettingsById } = useAlertSettings()

  const hasTriggeredAlerts = useMemo(
    () =>
      entityHasTriggeredAlerts(
        getPowerlineAlertSettingsWStatesByEntityId(Number(id)),
        orgActivePowerlinesAlertSettingsById,
      ),
    [
      getPowerlineAlertSettingsWStatesByEntityId,
      id,
      orgActivePowerlinesAlertSettingsById,
    ],
  )

  const powerlineTabsConfig = useMemo(
    () => getPowerlineTabsConfig(powerlineSearchParams),
    [powerlineSearchParams],
  )

  useEffect(() => {
    if (isError) {
      navigate(DEFAULT_TARGET)
    }
  }, [isError, navigate])

  return (
    <>
      {powerlineFetching || !powerline ? (
        <Spinner styles={{ my: 2 }} />
      ) : (
        <>
          <PreviousPage href={`${baseOrgURL}/powerlines`} />
          <Stack
            flexDirection="row"
            alignItems="start"
            justifyContent="space-between"
            sx={{ [theme.breakpoints.down("md")]: { flexDirection: "column" } }}
          >
            <PowerlineSummary powerline={powerline} />
            <DashboardDatePicker
              dateRange={powerlineDateRange}
              dispatchDateRange={dispatchPowerlineDateRange}
              entity="powerline"
              isFetching={arePlotsLoading}
            />
          </Stack>
          <LinkTabsSection
            tabsConfig={powerlineTabsConfig}
            badges={{ "tabs.ALERTS": hasTriggeredAlerts }}
          />
          <Outlet context={{ powerline, powerlineFetching, id }} />
          <ExitZoomModeButton entity="powerline" />
          <ScrollToTopButton />
        </>
      )}
    </>
  )
}

export default Powerline
